import * as React from "react";
import * as classnames from 'classnames';
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import Modal, { ModalSize } from "../../misc/components/modal";
import { QuestionnaireResults } from "./QuestionnaireResults";
import { NotesModalBody } from "../../misc/components/NotesModalBody";
import { Alert, DiarySession, WebPortalClient, Subject, Visit } from "webportalclient";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DiaryAlertsView } from "./DiaryAlertView";
import AlertComponent, { AlertCategoryEnum } from "../../misc/components/Alert";
import { useWindowSize } from "../../misc/Hooks/UseWindowSize";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

interface DiarySessionResultProps {
    subject: Subject;
    selectedDate: string;
    sessions: DiarySession[];
    loadingScopedSessions: boolean;
    onClose: () => void;
}

enum QualityFlags {
    SlowStart = 1,
    AbruptEnd = 2,
    CoughPresent = 4,
    ShortTest = 8,
    PoorRiseTime = 32,
    PoorHesitationTime = 64,
    FivcFvcQuality = 128,
    NoEofe = 256,
    FivcNull = 512
}

enum SessionResultTabs {
    Tests = "tests",
    Questionnaires = "questionnaires",
    Alerts = "alerts",
    Notes = "notes",
}

export const DiarySessionResult = ({ subject, selectedDate, sessions, loadingScopedSessions, onClose }: DiarySessionResultProps) => {
    const [currentTab, setCurrentTab] = React.useState<SessionResultTabs>(SessionResultTabs.Tests);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [isAddingNewNote, setIsAddingNewNote] = React.useState(false);
    const [visits, setVisits] = React.useState<Visit[]>([]);
    const [expandedSessions, setExpandedSessions] = React.useState<DiarySession[]>([]);
    const [alertsForSessions, setAlertsForSessions] = React.useState<Alert[]>([]);
    const [loadingExpanded, setLoadingExpanded] = React.useState<boolean>(false);
    const [notesError, setNotesError] = React.useState<string | undefined>();
    const { currentUserPermissions } = useSecurityContext();
    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddDiarySessionNotes);
    const hasViewNotePermission =  currentUserPermissions.some(x => x.Id == PermissionEnum.ViewDiarySessionNotes);

    const windowSize = useWindowSize(1200);

    const getSpecificSessionsExpanded = async () => {
        if (sessions.length !== 0 && !loadingScopedSessions) {
            setLoadingExpanded(true);
            const visitResults = await client.client.getVisits(undefined, `${sessions.map(s => `id eq ${s.visitId}`).join(" or ")}`);
            setVisits(visitResults.value);
            const sessionResults = await client.client.getDiarySessions(undefined, `${sessions.map(s => `id eq ${s.id}`).join(" or ")}`, undefined, "tests,notes,questionnaires($expand=answers)");
            setExpandedSessions(sessionResults.value);

            const alerts = await client.client.getAlerts(undefined, `${sessions.map(s => `triggeredBySessionId eq ${s.id}`).join(" or ")}`);
            setAlertsForSessions(alerts.value);
            setLoadingExpanded(false);
        }
    }

    React.useEffect(() => {
        getSpecificSessionsExpanded();
    }, [sessions, loadingScopedSessions]);

    const addNewNote = async (annotation: string) => {     
        try {
            setIsAddingNewNote(true);
            setNotesError(undefined);
            await legacyClient.putSessionNotes(expandedSessions[0].id, annotation);

            await getSpecificSessionsExpanded();
        } catch (ex) {
            setNotesError(ex.Message);
        }

        setIsAddingNewNote(false);
    }

    const qualityIndicator = (qualityFlag, acceptable) => {
        const qualityError = () => {
            return (
                <>
                    {(qualityFlag & QualityFlags.SlowStart) === QualityFlags.SlowStart ? <><span className="label label-danger">Slow Start</span>{" "}</> : null}
                    {(qualityFlag & QualityFlags.CoughPresent) === QualityFlags.CoughPresent ? <><span className="label label-danger">Cough Present</span>{" "}</> : null}
                    {(qualityFlag & QualityFlags.PoorRiseTime) === QualityFlags.PoorRiseTime ? <><span className="label label-danger">Poor Rise Time</span>{" "}</> : null}
                </>
            );
        }

        return (
            <>
                {acceptable ? <i className="text-success fas fa-check"></i> : <i className="text-danger fas fa-times"></i>}&nbsp;
                {qualityError()}
            </>
        );
    }

    const getSessionRecord = (key: string, session: DiarySession) => {
        return (
            <React.Fragment key={key}>
                <div className="row">
                    <div className="col-sm-12">
                        <h4 className="text-primary">
                            Visit {visits.find(v => v.id === session.visitId)?.name} Session {session.name} performed on <LocalizedDateTime time={session.occurred} accuracy={DateTimeAccuracy.Second} isTooltip={true} />
                        </h4>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        {session.tests.length > 0 ?
                            <table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th>Test</th>
                                        <th>Date / Time</th>
                                        <th>PEF (L/min)</th>
                                        <th>FEV1 (L)</th>
                                        <th>Acceptability</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {session.tests.sort((a, b) => a.index - b.index).map(t => (
                                        <tr key={`${key}_Test${t.index}`}>
                                            <th>{t.index + 1}</th>
                                            <td><LocalizedDateTime time={t.occurred} accuracy={DateTimeAccuracy.Second} isTooltip={true} /></td>
                                            <td>{t.pef ? t.pef : "-"}</td>
                                            <td>{t.fev1 ? t.fev1.toFixed(2) : "-"}</td>
                                            <td>{qualityIndicator(t.qualityIndicators, t.accepted)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table> : <AlertComponent show={true} category={AlertCategoryEnum.Info}>
                                There are no tests for this session
                            </AlertComponent>}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    const modalBody = () => {

        const notes = expandedSessions.map(s => s.notes).flat();

        return (
            <div className="modal-body tabs-container">
                <ul className="nav nav-tabs">
                    <li className={classnames("nav-item", { active: currentTab === SessionResultTabs.Tests })}>
                        <a className="nav-link" onClick={() => setCurrentTab(SessionResultTabs.Tests)}>Test Results</a>
                    </li>
                    <li className={classnames("nav-item", { active: currentTab === SessionResultTabs.Questionnaires })}>
                        <a className="nav-link" onClick={() => setCurrentTab(SessionResultTabs.Questionnaires)}>Questionnaire Results</a>
                    </li>
                    <li className={classnames("nav-item", { active: currentTab === SessionResultTabs.Alerts })}>
                        <a className="nav-link" onClick={() => setCurrentTab(SessionResultTabs.Alerts)}>
                            Alerts&nbsp;
                            {alertsForSessions.length > 0 ? <span className="badge badge-danger">{alertsForSessions.length}</span> : null}
                        </a>
                    </li>
                    {
                        hasViewNotePermission? 
                        <li className={classnames("nav-item", { active: currentTab === SessionResultTabs.Notes })}>
                            <a className="nav-link" onClick={() => setCurrentTab(SessionResultTabs.Notes)}>
                                Notes&nbsp;
                                {notes.length > 0 ? <span className="badge badge-primary">{notes.length}</span> : null}
                            </a>
                        </li> : null
                    }
                </ul>
                <div className="tab-content">
                    <div className="row">
                        <div className="col-sm-12">
                            {currentTab === SessionResultTabs.Tests ? expandedSessions.map(s => getSessionRecord(`SessionRecord${s.id}`, s)) : null}
                            {currentTab === SessionResultTabs.Questionnaires ? <QuestionnaireResults visits={visits} sessions={expandedSessions} /> : null}
                            {currentTab === SessionResultTabs.Alerts ? <DiaryAlertsView alerts={alertsForSessions} sessions={expandedSessions} /> : null}
                            {currentTab === SessionResultTabs.Notes && hasViewNotePermission ?
                                <NotesModalBody
                                    notes={notes}
                                    disabled={!hasAddingNotePermission}
                                    onAdd={(annotation) => addNewNote(annotation)}
                                    isAddingNote={isAddingNewNote && hasAddingNotePermission}
                                    errorStatus={notesError}
                                    onCloseModal={onClose}
                                />
                                : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Modal show={true} header={<>Subject {subject.number} - Diary Session - <LocalizedDateTime time={selectedDate} accuracy={DateTimeAccuracy.Day} /></>} size={windowSize ? ModalSize.Large : ModalSize.Normal} closeModalHandler={onClose} automationId="DiarySessionResult">
            {loadingExpanded || loadingScopedSessions ? <LoadingWithMessage message="Loading Diary Records..." /> : modalBody()}
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={onClose}>Close</button>
            </div>
        </Modal>
    );
}
